import React, { useMemo, useState } from 'react';

import { useStyles } from './AllExercises.styles';
import theme from '../../../styles/theme';
import { useLazyLoadGeneral } from '../../../hooks/use-lazy-load-general';
import { IExercise } from '../../../types/interfaces/exercise';
import { Box } from '@mui/material';
import Search from '../../../components/reusable/search/Search';
import { useLazyGetAllExercises } from '../../../services/redux/api/exercisesAPI';
import ExerciseList from '../exercise-public-requests/exercise-list/ExerciseList';
import { useExercise } from '../useExercise';
import ExercisesListWithPanel from '../exercises-list-with-panel/ExercisesListWithPanel';
import Exercise from '../exercise/Exercise';
import NewExercise from '../new-exercise/NewExercise';
import type { ExercisePanelMode } from '../exercises-list-with-panel/ExercisesListWithPanel';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useDeleteExercise } from '../../../services/redux/api/exercisesAPI';
import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import { ExerciseScope } from '../../../shared/constants';
import { useDebounce } from '../../../hooks/use-debounce';

type VisibilityFilterValue = 'all' | 'public' | 'private';
const NULL_OWNER_USER_FILTER_ID = '__null-owner__';

export default function AllExercises() {
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, 500);
  const [visibilityFilter, setVisibilityFilter] =
    useState<VisibilityFilterValue>('all');
  const [userFilterId, setUserFilterId] = useState<string>('all');
  const { t } = useTranslation();
  const lazyQueryParams = useMemo(
    () => ({
      ...(visibilityFilter !== 'all'
        ? { scope: visibilityFilter as ExerciseScope }
        : {}),
    }),
    [visibilityFilter],
  );
  const { items: exercises, footerRef } = useLazyLoadGeneral<IExercise>(
    useLazyGetAllExercises,
    debouncedFilter,
    15,
    lazyQueryParams,
  );
  const users = useMemo(() => {
    const usersMap = new Map<string, { id: string; name: string }>();
    let hasNullOwner = false;

    exercises.forEach((exercise) => {
      if (!exercise.owner?.id) {
        hasNullOwner = true;
        return;
      }

      usersMap.set(exercise.owner.id, {
        id: exercise.owner.id,
        name: `${exercise.owner.firstname || ''} ${
          exercise.owner.lastname || ''
        }`.trim(),
      });
    });

    const usersList = [...usersMap.values()].filter((user) => !!user.name);

    if (!hasNullOwner) {
      return usersList;
    }

    return [
      {
        id: NULL_OWNER_USER_FILTER_ID,
        name: t('moderatorDashboard.publicOwnerFilter_public'),
      },
      ...usersList,
    ];
  }, [exercises, t]);
  const filteredExercises = useMemo(() => {
    return exercises.filter((exercise) => {
      const matchesUser =
        userFilterId === 'all' ||
        (userFilterId === NULL_OWNER_USER_FILTER_ID && !exercise.owner?.id) ||
        exercise.owner?.id === userFilterId;

      return matchesUser;
    });
  }, [exercises, userFilterId]);
  const { classes } = useStyles(theme);
  const { renderExerciseActionButton } = useExercise();
  const [deleteExercise] = useDeleteExercise();
  const [exerciseToDeleteId, setExerciseToDeleteId] = useState<string>(null);

  const [panelState, setPanelState] = useState<{
    exerciseId: string | null;
    mode: ExercisePanelMode;
  }>({ exerciseId: null, mode: 'view' });

  const panelOpen = panelState.exerciseId !== null || panelState.mode === 'new';

  function openPanel(exerciseId: string | null, mode: ExercisePanelMode) {
    setPanelState({ exerciseId, mode });
  }

  function closePanel() {
    setPanelState({ exerciseId: null, mode: 'view' });
  }

  function onEditClick(id: string) {
    openPanel(id, 'edit');
  }

  function onExerciseClick(exercise: IExercise) {
    openPanel(exercise.id, 'view');
  }

  function onDelete(id: string) {
    deleteExercise(id)
      .unwrap()
      .then(() => {
        setExerciseToDeleteId(null);
        if (panelState.exerciseId === id) {
          closePanel();
        }
      })
      .catch((e) => console.log(e));
  }

  const listContent = (
    <Box>
      <Search
        fullWidth
        value={filter}
        onChange={setFilter}
        className={classes.exercisesListSearch}
      />
      <Box className={classes.filtersRow}>
        <AppSelectWrapper
          label={t('common.user')}
          defaultValue={
            [{ id: 'all', name: t('common.all') }, ...users].find(
              (option) => option.id === userFilterId,
            ) || null
          }
          options={[{ id: 'all', name: t('common.all') }, ...users]}
          onChange={(e) => setUserFilterId(e?.target?.value?.id || 'all')}
          disableClear
          disableSorting
        />
        <AppSelectWrapper
          label={t('moderatorDashboard.visibility')}
          defaultValue={
            [
              { id: 'all', name: t('common.all') },
              { id: 'private', name: t('exercises.scope_private') },
              { id: 'public', name: t('exercises.scope_public') },
            ].find((option) => option.id === visibilityFilter) || null
          }
          options={[
            { id: 'all', name: t('common.all') },
            { id: 'private', name: t('exercises.scope_private') },
            { id: 'public', name: t('exercises.scope_public') },
          ]}
          onChange={(e) => setVisibilityFilter(e?.target?.value?.id || 'all')}
          disableClear
          disableSorting
        />
      </Box>

      <ExerciseList
        exercises={filteredExercises}
        showSearch={false}
        showPublicStatus
        getActionButtons={(exercise) =>
          renderExerciseActionButton(exercise, onEditClick, (id) =>
            setExerciseToDeleteId(id),
          )
        }
        onExerciseClick={onExerciseClick}
      />
      <Box ref={footerRef} mb={1} />
      <ConfirmDialog
        open={exerciseToDeleteId}
        onClose={() => setExerciseToDeleteId(null)}
        title={t('common.delete')}
        onSubmit={() => {
          onDelete(exerciseToDeleteId);
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </Box>
  );

  const panelContent = panelOpen ? (
    panelState.mode === 'new' ? (
      <NewExercise exerciseId={null} onBack={closePanel} />
    ) : panelState.mode === 'edit' ? (
      <NewExercise exerciseId={panelState.exerciseId} onBack={closePanel} />
    ) : (
      <Exercise
        exerciseId={panelState.exerciseId}
        onBack={closePanel}
        onEditClick={() =>
          panelState.exerciseId && openPanel(panelState.exerciseId, 'edit')
        }
      />
    )
  ) : null;

  return (
    <ExercisesListWithPanel
      listContent={listContent}
      panelContent={panelContent}
      panelOpen={panelOpen}
      onPanelClose={closePanel}
    />
  );
}
